import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileDown, Loader2 } from "lucide-react";
import { BULK_ACTION_TYPES, BULK_ACTION_TYPE_LABEL, type BulkAction } from "@somasi/shared";

import { api } from "@/integrations/aws/resources";
import { getBulkActionFileUrl } from "@/integrations/aws/api";
import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  FilterSelect,
  ListPage,
  ListPageHeader,
  ListPageTable,
  ListSearch,
  ListToolbar,
  SortHeader,
  TableMessageRow,
  TablePagination,
  useDebounced,
  useListSort,
  usePagination,
} from "@/components/list-controls";
import { BulkActionStatusBadge, StartedBy, formatStamp } from "@/lib/bulk-actions";
import { notify } from "@/lib/notify";

export const Route = createFileRoute("/_authenticated/bulk-actions/")({
  head: () => ({
    meta: [
      { title: "Bulk Actions — Somasi Ops" },
      {
        name: "description",
        content: "Every bulk action run in Somasi Ops, where it has got to, and the files it left.",
      },
    ],
  }),
  component: BulkActionsPage,
});

/**
 * The register of bulk actions: who did what in bulk, whether it is still going,
 * and the files it left — downloadable from here for as long as the action is
 * listed, whatever the emailed link's lifetime.
 */
function BulkActionsPage() {
  const [search, setSearch] = useState("");
  const [type, setType] = useState<"all" | BulkAction["type"]>("all");
  const [status, setStatus] = useState<"all" | BulkAction["status"]>("all");
  /** The file being fetched, as `${actionId}:${index}`, so its button can say so. */
  const [preparing, setPreparing] = useState<string | null>(null);

  const query = useDebounced(search.trim());
  const filters = { query, type, status };
  const sort = useListSort({ column: "created_at", ascending: false });
  const pager = usePagination({ resetKey: { ...filters, sort: sort.sort } });
  const filtering = Boolean(query) || type !== "all" || status !== "all";

  const {
    data: result,
    isLoading,
    error: loadError,
  } = useQuery({
    queryKey: [
      "bulk_actions",
      { ...filters, sort: sort.sort, page: pager.page, pageSize: pager.pageSize },
    ],
    queryFn: async () => {
      const list = api.bulkActions
        .list()
        .order(sort.sort.column, { ascending: sort.sort.ascending });
      if (type !== "all") list.eq("type", type);
      if (status !== "all") list.eq("status", status);
      if (query) {
        list.or([
          { column: "title", op: "ilike", value: `%${query}%` },
          { column: "created_by_name", op: "ilike", value: `%${query}%` },
          { column: "created_by_email", op: "ilike", value: `%${query}%` },
        ]);
      }
      const { data, error, count } = await list.page(pager.page, pager.pageSize);
      if (error) throw error;
      return { rows: (data ?? []) as BulkAction[], total: count ?? 0 };
    },
    placeholderData: (previous) => previous,
    // Kept current while anything on the page is still going, so a run started
    // a minute ago moves to done without a reload.
    refetchInterval: (q) =>
      q.state.data?.rows.some((r) => r.status === "queued" || r.status === "running")
        ? 5000
        : false,
  });

  const rows = result?.rows ?? [];

  const download = async (action: BulkAction, index: number) => {
    const key = `${action.id}:${index}`;
    setPreparing(key);
    try {
      window.location.assign(await getBulkActionFileUrl(action.id, index));
    } catch (error) {
      notify.error(error);
    } finally {
      setPreparing(null);
    }
  };

  return (
    <ListPage>
      <ListPageHeader>
        <PageHeader
          title="Bulk Actions"
          description="Everything run in bulk, by anyone — where it has got to, and the files it left."
        />
        <ListToolbar>
          <ListSearch value={search} onChange={setSearch} placeholder="Search title or person…" />
          <FilterSelect
            value={type}
            onChange={(v) => setType(v as typeof type)}
            options={[
              { value: "all", label: "All types" },
              ...BULK_ACTION_TYPES.map((t) => ({ value: t, label: BULK_ACTION_TYPE_LABEL[t] })),
            ]}
          />
          <FilterSelect
            value={status}
            onChange={(v) => setStatus(v as typeof status)}
            options={[
              { value: "all", label: "All statuses" },
              { value: "queued", label: "Queued" },
              { value: "running", label: "In progress" },
              { value: "done", label: "Done" },
              { value: "failed", label: "Failed" },
            ]}
          />
        </ListToolbar>
      </ListPageHeader>

      <ListPageTable>
        <Table containerClassName="min-h-0 flex-1">
          <TableHeader sticky>
            <TableRow>
              <SortHeader sort={sort} column="title">
                Title
              </SortHeader>
              <SortHeader sort={sort} column="type">
                Type
              </SortHeader>
              <SortHeader sort={sort} column="status">
                Status
              </SortHeader>
              <TableHead>Result</TableHead>
              <SortHeader sort={sort} column="created_by_name">
                Started by
              </SortHeader>
              <SortHeader sort={sort} column="created_at">
                Started
              </SortHeader>
              <TableHead className="text-right">Files</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableMessageRow colSpan={7}>Loading…</TableMessageRow>
            ) : loadError && !result ? (
              <TableMessageRow colSpan={7}>
                Could not load the bulk actions: {(loadError as Error).message}
              </TableMessageRow>
            ) : rows.length === 0 ? (
              <TableMessageRow colSpan={7}>
                {filtering ? "No bulk actions match these filters." : "No bulk actions yet."}
              </TableMessageRow>
            ) : (
              rows.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-medium">
                    <Link
                      to="/bulk-actions/$actionId"
                      params={{ actionId: a.id }}
                      className="text-primary hover:underline"
                    >
                      {a.title}
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm">
                    {BULK_ACTION_TYPE_LABEL[a.type] ?? a.type}
                  </TableCell>
                  <TableCell>
                    <BulkActionStatusBadge status={a.status} />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {a.error ?? a.summary ?? "—"}
                  </TableCell>
                  <TableCell className="text-sm">
                    <StartedBy action={a} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">
                    {formatStamp(a.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    {a.files.length === 0 ? (
                      <span className="text-sm text-muted-foreground">—</span>
                    ) : (
                      <div className="flex flex-col items-end gap-1">
                        {a.files.map((f, index) => (
                          <Button
                            key={f.key}
                            size="sm"
                            variant="outline"
                            title={f.name}
                            disabled={preparing === `${a.id}:${index}`}
                            onClick={() => void download(a, index)}
                          >
                            {preparing === `${a.id}:${index}` ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <FileDown className="mr-2 h-4 w-4" />
                            )}
                            {a.files.length === 1 ? "Download PDF" : f.name}
                          </Button>
                        ))}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          pagination={pager}
          total={result?.total}
          loading={isLoading}
          unit="bulk action"
        />
      </ListPageTable>
    </ListPage>
  );
}
